/**
 * CWR 1.2 — the Home header. It always shows the studio, never the signed-in client
 * (retire-greeting-header, Gabriel 2026-11-03: "we should retire showing a clients name in the
 * header and not the studio name").
 *
 *   LOGO + logoUrl is owned by ClientLogoLockupHeader in the shell.
 *   NAME, or LOGO + null logoUrl → centered text wordmark from business.name, with the
 *                        descriptor line under it when set (fail-soft — never an empty header).
 *
 * Data comes from the 1.1 dashboard payload the ClientShell already owns — this
 * component renders, it does not fetch.
 */
import { useLayoutEffect, useRef } from 'react';
import type { DashboardBusiness } from '@/types/dashboard';
import styles from './ClientNameHeader.module.css';

export interface ClientNameHeaderProps {
  business: DashboardBusiness;
}

/** The wordmark's size in the stylesheet; the name is drawn smaller only when it would not fit on one line. */
const WORDMARK_PX = 32;

/** Keeps the studio's name on one line (Gabriel 2026-11-03): measured at full size, scaled down to the header's width. */
function useOneLineWordmark(name: string, headerMode: DashboardBusiness['headerMode']) {
  const ref = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const mark = ref.current;
    const box = mark?.parentElement;
    if (!mark || !box) return;
    let disposed = false;

    const fit = () => {
      if (disposed) return;
      mark.style.fontSize = '';
      const natural = mark.getBoundingClientRect().width;
      const room = box.clientWidth;
      if (room > 0 && natural > room) {
        mark.style.fontSize = `${Math.floor((WORDMARK_PX * room * 10) / natural) / 10}px`;
      }
    };

    fit();
    // The studio's heading font can arrive after the first draw and change the name's width.
    void (document.fonts?.ready ?? Promise.resolve()).then(fit);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
    observer?.observe(box);
    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, [name, headerMode]);

  return ref;
}

export function ClientNameHeader({ business }: ClientNameHeaderProps) {
  const wordmarkRef = useOneLineWordmark(business.name, business.headerMode);

  return (
    <div className={styles.logoHeader} data-testid="client-header-logo">
      <span ref={wordmarkRef} className={styles.wordmark} data-testid="client-header-wordmark">
        {business.name}
      </span>
      {business.descriptor ? (
        <span className={styles.descriptor} data-testid="client-header-descriptor">{business.descriptor}</span>
      ) : null}
    </div>
  );
}
